import React, {lazy} from "react";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import {BrowserRouter as Router, Navigate, Route, Routes} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {BrowserTracing} from "@sentry/tracing";
import {
	BASE_URL,
	PREPROD_URL_EN,
	PREPROD_URL_FR,
	PROD_URL_EN,
	PROD_URL_EN_INTERNAL,
	PROD_URL_FR,
	PROD_URL_FR_INTERNAL,
	SENTRY_DOMAIN_ALLOWED,
	UAT_URL_EN,
	UAT_URL_FR,
} from "modules/constants";
import store from "modules/store";
import {Provider} from "react-redux";
import {ThemeProvider} from "@mui/material";
import {theme} from "assets/theme";
import {
	ErrorModal,
	HelpLayout,
	HocI18n,
	HocData,
	Layout,
	PagePreloader,
	SecretKeyLayout,
} from "components";
import {retryFailLoad} from "modules/utils/lazyLoad";

import "assets/css/reset.css";
import "assets/fonts/fonts.css";
import "assets/css/core.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

let errorCount = 0;
const MAX_ERRORS = 100;
Sentry.init({
	dsn: "https://d79658cb7e674611abd3440f29193e0d@o151969.ingest.sentry.io/6727015",
	environment: process.env.REACT_APP_SENTRY_ENV || "development",
	sampleRate: 0.1,
	tracesSampleRate: 0.1,
	allowUrls: [
		UAT_URL_EN,
		UAT_URL_FR,
		PREPROD_URL_EN,
		PREPROD_URL_FR,
		PROD_URL_EN_INTERNAL,
		PROD_URL_FR_INTERNAL,
		PROD_URL_EN,
		PROD_URL_FR,
		SENTRY_DOMAIN_ALLOWED,
	],
	integrations: [
		new BrowserTracing(),
		new Sentry.Integrations.Breadcrumbs({
			console: false,
		}),
	],
	denyUrls: [
		"quantcast",
		"xsca",
		// browser's extensions
		/extensions\//i,
		/^chrome:\/\//i,
		/^moz-extension:\/\//i,
	],
	beforeSend: (event: Sentry.Event) => {
		errorCount++;
		if (errorCount > MAX_ERRORS) {
			return null;
		}
		return event;
	},
});

const Landing = lazy(retryFailLoad(() => import("pages/Landing")));
const Picks = lazy(retryFailLoad(() => import("pages/Picks")));
// const FanVote = lazy(retryFailLoad(() => import("pages/FanVote")));
const Help = lazy(retryFailLoad(() => import("pages/Help")));
const Stats = lazy(() => import("pages/stats/Stats"));
const LeagueLeaders = lazy(() => import("pages/LeagueLeaders"));

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
	<React.StrictMode>
		<Provider store={store}>
			<ThemeProvider theme={theme}>
				<HocI18n>
					<Router basename={BASE_URL}>
						<React.Suspense fallback={<PagePreloader />}>
							<HocData>
								<Routes>
									<Route element={<SecretKeyLayout />}>
										<Route element={<Layout />}>
											<Route path="/" element={<Landing />} />
											<Route path="/picks" element={<Picks />} />
											{/*<Route path="/fan-vote" element={<FanVote />} />*/}
											<Route path="/stats" element={<Stats />} />
											<Route
												path="/league-leaders"
												element={<LeagueLeaders />}
											/>

											<Route path="/help" element={<HelpLayout />}>
												<Route index element={<Help />} />
												<Route path=":section" element={<Help />} />
											</Route>

											<Route path="*" element={<Navigate to="/" />} />
										</Route>
									</Route>
								</Routes>

								<ErrorModal />
							</HocData>
						</React.Suspense>
					</Router>
				</HocI18n>
			</ThemeProvider>
		</Provider>
	</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
